import React, { useEffect, useRef, useState } from 'react';
import AspectRatio from '@jbhi-fi/lanyard-ui/components/AspectRatio';
import { palette } from '@jbhi-fi/lanyard-ui/themes';

import { LoadingSkeleton } from '../LoadingSkeleton/LoadingSkeleton';
import { useYoutubePlayer, UseYoutubePlayerProps } from '../hooks/useYouTubePlayer/useYouTubePlayer';
import { PlayIllustrated } from '@jbhi-fi/lanyard-ui/components/Icons';
import { styles } from './YouTubePlayer.css';
import { classNames } from '@jbhi-fi/lanyard-ui/global/utils';

export type Orientation = 'Landscape' | 'Portrait';

export type YoutubePlayerProps = Pick<UseYoutubePlayerProps, 'onPlayerLoaded' | 'onStateChange'> & {
  videoUrl?: string;
  orientation?: Orientation;
  landscapeThumbnail?: React.ReactElement;
  portraitThumbnail?: React.ReactElement;
  isOrientationFixed?: boolean;
  classes?: string;
};

export const PLAYER_STATE: { [key: number]: string } = {
  1: 'play',
  2: 'pause',
  3: 'buffering',
};

export const ASPECT_RATIOS = {
  landscape: 16 / 9,
  portrait: 9 / 16,
};

const getCustomThumbnailStatus = (
  isOrientationFixed: boolean,
  orientation: Orientation,
  landscapeThumbnail?: React.ReactElement,
  portraitThumbnail?: React.ReactElement
) => {
  if (isOrientationFixed) {
    return orientation === 'Landscape' ? !!landscapeThumbnail : !!portraitThumbnail;
  }
  return !!landscapeThumbnail && !!portraitThumbnail;
};

export const YoutubePlayer: React.FC<YoutubePlayerProps> = ({
  videoUrl = '',
  orientation = 'Landscape',
  isOrientationFixed = false,
  onPlayerLoaded,
  onStateChange,
  landscapeThumbnail,
  portraitThumbnail,
  classes,
}) => {
  const hasCustomThumbnails = getCustomThumbnailStatus(
    isOrientationFixed,
    orientation,
    landscapeThumbnail,
    portraitThumbnail
  );

  const [shouldShowCustomThumbnail, setShouldShowCustomThumbnail] = useState(hasCustomThumbnails);
  const [isThumbnailFading, setIsThumbnailFading] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const onStateChangeCallback = (event: { data: number }) => {
    if (PLAYER_STATE[event.data] === 'buffering') {
      setIsThumbnailFading(true);
    }
    onStateChange?.(event);
  };

  useYoutubePlayer({ videoUrl, element: ref, onPlayerLoaded, onStateChange: onStateChangeCallback });

  useEffect(() => {
    if (isThumbnailFading) {
      const timeout = setTimeout(() => {
        setIsThumbnailFading(false);
        setShouldShowCustomThumbnail(false);
      }, 500);
      return () => clearTimeout(timeout);
    }
  }, [isThumbnailFading]);

  return (
    <>
      {shouldShowCustomThumbnail ? (
        <div className={classNames([styles.customVideoThumbnail({ isThumbnailFading }), classes])}>
          <div className={styles.thumbnailClickBlocker} />
          {orientation === 'Landscape' ? landscapeThumbnail : portraitThumbnail}
          <PlayIllustrated className={styles.playButton} width={70} height={70} />
        </div>
      ) : null}
      <div className="chromatic-ignore">
        <AspectRatio ratio={orientation === 'Portrait' ? ASPECT_RATIOS.portrait : ASPECT_RATIOS.landscape}>
          <div ref={ref}>
            {/*The YouTube iframe will replace this whole div after it's loaded.*/}
            <LoadingSkeleton.Box backgroundColor={palette.secondary.main} />
          </div>
        </AspectRatio>
      </div>
    </>
  );
};
